/**
 * Lightweight reverse-geocoder for the citizen home screen review page.
 *
 * Hits the same Nominatim service the backend uses, but client-side
 * so we don't need a new authenticated endpoint just for a label. We
 * cache results in sessionStorage so each session warms up after the
 * first lookup and subsequent reviews are instant.
 *
 * On any failure we resolve with `null` and the caller falls back to
 * "Position détectée + coordinates" — never blocks the alert flow.
 */
const CACHE_KEY = "sosmali.geolabel.cache";
const NOMINATIM = "https://nominatim.openstreetmap.org/reverse";

const readCache = () => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}"); }
  catch (_e) { return {}; }
};
const writeCache = (obj) => {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch (_e) { /* noop */ }
};

const key = (lat, lng) => `${lat.toFixed(3)},${lng.toFixed(3)}`;

const compose = (addr) => {
  const locality =
    addr.village || addr.hamlet || addr.town || addr.suburb ||
    addr.city_district || addr.city || addr.municipality;
  const region = addr.state || addr.region;
  const country = addr.country;
  if (locality && country) return `${locality}, ${country}`;
  if (locality) return locality;
  if (region && country) return `${region}, ${country}`;
  return null;
};

export const resolveLocationLabel = async (lat, lng) => {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  const k = key(lat, lng);
  const cache = readCache();
  if (Object.prototype.hasOwnProperty.call(cache, k)) return cache[k];

  try {
    const params = new URLSearchParams({
      lat: lat.toFixed(6), lon: lng.toFixed(6),
      format: "jsonv2", "accept-language": "fr", zoom: "14", addressdetails: "1",
    });
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 4000);
    const r = await fetch(`${NOMINATIM}?${params}`, {
      signal: ctrl.signal,
      headers: { "Accept": "application/json" },
    });
    clearTimeout(timeout);
    if (!r.ok) { cache[k] = null; writeCache(cache); return null; }
    const data = await r.json();
    const label = compose(data.address || {});
    cache[k] = label || null;
    writeCache(cache);
    return label || null;
  } catch (_e) {
    return null;
  }
};
